import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { useUpdateChild } from '../../api/student/studentQueries.js';
import { toast } from 'react-toastify';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { SlidePanel } from '../ui/slide-panel';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';

export function StudentEditPanel({ student, isOpen, onClose }) {
  const { mutate: updateStudent, isPending: isUpdating } = useUpdateChild();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [status, setStatus] = useState("active");
  const [profileImage, setProfileImage] = useState("/placeholder.svg");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (student) {
      setFirstName(student.firstName || "");
      setLastName(student.lastName || "");
      setStatus(student.status || "active");
      setProfileImage(student.photoUrl || "/placeholder.svg");
    }
  }, [student]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!student?.id) {
      toast.error('Student ID is missing');
      return;
    }

    const data = {
      firstName,
      lastName,
      photoUrl: profileImage,
      status
    };
    
    updateStudent(
      { id: student.id, data },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (error) => {
          toast.error(error.response?.data?.message || 'Failed to update student details');
        }
      }
    );
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChangeImage = () => {
    fileInputRef.current?.click();
  };

  const renderFooter = () => (
    <div className="flex justify-center gap-4">
      <Button 
        type="button" 
        variant="outline" 
        className="w-full sm:w-40 text-purple-500 border-purple-200"
        onClick={onClose}
        disabled={isUpdating}
      >
        Cancel
      </Button>
      <Button 
        type="submit" 
        form="student-edit-form"
        className="w-full sm:w-40 bg-purple-500 hover:bg-purple-600 text-white"
        disabled={isUpdating}
      >
        {isUpdating ? (
          <div className="flex items-center gap-2">
            <LoadingSpinner size="sm" className="text-white" />
            Saving...
          </div>
        ) : (
          'Save Changes'
        )}
      </Button>
    </div>
  );

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Student Details"
      footer={renderFooter()}
    >
      <form id="student-edit-form" onSubmit={handleSubmit} className="h-full flex flex-col">
        <div className="flex-1 p-6 space-y-8">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleImageSelect}
          />
          
          <div className="flex flex-col items-center">
            <div className="relative mb-4">
              <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-gray-200">
                <img
                  src={profileImage}
                  alt="Student profile"
                  className="object-cover w-full h-full"
                />
              </div>
            </div>
            <Button 
              type="button"
              className="bg-purple-500 hover:bg-purple-600 text-white" 
              onClick={handleChangeImage}
            >
              Change Image
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </form>
    </SlidePanel>
  );
}